import React, { useEffect, useState } from "react";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { formatDate } from "../../data/format";
import { EmptyState, SkeletonText, FileIcon } from "../../ui";

/**
 * Documents tab (staff only — D2 keeps documents off the referral tier;
 * referral own-uploads arrive with the Phase-2.5 intake flow).
 *
 * Two data paths:
 * - FEATURES.documentsByOwner ON → the pre-agreed Session-H contract
 *   GET /api/documents/owner/Client/:id (bare array, newest first).
 * - OFF (today's deployed backend) → POST /api/documents/group with the
 *   doc ids collected from client.documents + each policy's documents
 *   array. Those arrays are EMPTY on merged records by design, so imported
 *   documents can't be listed yet — the notice says so instead of showing
 *   a silent blank.
 */
function DocumentsTab({ household }) {
  const [state, setState] = useState({ loading: true, docs: [], failed: false });

  useEffect(() => {
    let cancelled = false;
    const done = (docs, failed = false) => {
      if (!cancelled) {
        setState({ loading: false, docs, failed });
      }
    };

    if (FEATURES.documentsByOwner) {
      API.getDocumentsByOwner("Client", household.id)
        .then((res) => done(Array.isArray(res.data) ? res.data : []))
        .catch(() => done([], true));
    } else {
      const ids = [
        ...(household.client.documents || []),
        ...household.policies.flatMap((p) => p.documents || [])
      ].map(String);
      if (ids.length === 0) {
        done([]);
      } else {
        API.getDocumentsByIdArray(ids)
          .then((res) => done(Array.isArray(res.data) ? res.data : []))
          .catch(() => done([], true));
      }
    }
    return () => {
      cancelled = true;
    };
  }, [household]);

  if (state.loading) {
    return <SkeletonText lines={4} />;
  }

  const notice = !FEATURES.documentsByOwner ? (
    <p className="feat-notice">
      Imported documents aren't listed yet — they'll appear here once the documents-by-owner
      endpoint ships (backend work in progress).
    </p>
  ) : null;

  if (state.failed) {
    return (
      <>
        {notice}
        <EmptyState
          icon={<FileIcon size={32} />}
          title="Documents couldn't be loaded"
          message="Something went wrong fetching this household's documents — try again from the browser refresh for now."
        />
      </>
    );
  }

  if (state.docs.length === 0) {
    return (
      <>
        {notice}
        <EmptyState
          icon={<FileIcon size={32} />}
          title="No documents yet"
          message="Upload a declarations page or policy document from the legacy Document Vault for now — uploads move here in a later phase."
        />
      </>
    );
  }

  return (
    <>
      {notice}
      <div className="feat-feed" role="list">
        {state.docs.map((doc) => {
          const body = (
            <>
              <span className="feat-policy-card__icon" style={{ width: 28, height: 28 }}>
                <FileIcon size={14} />
              </span>
              <span className="feat-feed__main">
                <span className="feat-feed__title">{doc.docName || "Untitled document"}</span>
                <span className="feat-feed__meta">
                  {[doc.type, formatDate(doc.created_at)].filter(Boolean).join(" · ")}
                  {doc.uploadStatus === "NOT-UPLOADED"
                    ? " · metadata only (binary not ingested)"
                    : ""}
                </span>
              </span>
            </>
          );
          return doc.document ? (
            <a
              key={doc._id}
              className="feat-feed__row"
              href={doc.document}
              target="_blank"
              rel="noreferrer"
              role="listitem"
            >
              {body}
            </a>
          ) : (
            <div
              key={doc._id}
              className="feat-feed__row"
              style={{ cursor: "default" }}
              role="listitem"
            >
              {body}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default DocumentsTab;
